import { Badge, Button, Table, Tag, Tooltip } from 'antd';
import { FlagFilled, ReloadOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { serverFetch } from '@server';
import { showError, fmtDate, fmtDateTime } from '@extensions/utils';
import { useUrlFilters } from '@extensions/url-filters';
import { useSelectCompany } from '@extensions/company';
import { TASK_PRIORITIES } from '../tasks/task-priority';
import { QUADRANTS, byPlanDate } from '../tasks/dashboard';
import Task from '../tasks/task';
import WeekPlan, { useWeekPlanIds, WeekPlanStar } from '../tasks/week-plan';
import Contragent from '../contragents/contragent';
import {
    PULSE_PRIORITIES, activityLabel, defaultPriority, dueState, isReleaseOverdue, isStale, priorityCounts,
    rankRows, ratedOpen, splitCompanies,
} from './pulse-utils';
import '../tasks/dashboard.css';
import './pulse.css';

const LIMIT = 8;

// Фильтр в URL: ?c= — фирма (0 — все)
const FILTER_SPEC = { companyId: { param: 'c', type: 'int', empty: 0 } };

const PRIORITY_COLORS = Object.fromEntries(QUADRANTS.map((q) => [q.priority, q.color]));

const COUNTER_KEYS = ['qtyOpen', 'qtyOld', 'qtyToday', 'qtyDone7', 'qtyNoUser', 'qtyNoDate'];

const companyHint = (c) => [
    `Открыто ${c.qtyOpen}`, `просрочено ${c.qtyOld}`, `сегодня ${c.qtyToday}`, `закрыто за 7 дн ${c.qtyDone7}`,
    `без исполнителя ${c.qtyNoUser}`, `без срока ${c.qtyNoDate}`,
].join(' · ');

const projectFilter = (r) => `project=${r.projectId ?? -1}`;
const userFilter = (r) => `user=${r.userId ?? -1}`;

const HealthDot = ({ health }) => (
    <Tooltip title={health.reason}>
        <i className={`pulse-dot ${health.level}`} role="img" aria-label={health.reason} />
    </Tooltip>
);

const Count = ({ value, className }) => (value > 0
    ? <span className={className}>{value}</span>
    : <span className="pulse-muted">—</span>);

// Мои открытые задачи с оценкой по квадрантам Эйзенхауэра
const MyTasks = ({ tasks, now, showCompany, loading, onOpen, weekPlan }) => {
    const [priority, setPriority] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const counts = useMemo(() => priorityCounts(tasks), [tasks]);
    const active = priority ?? defaultPriority(counts);
    const list = useMemo(
        () => tasks.filter((task) => task.priority === active).sort(byPlanDate),
        [tasks, active],
    );
    const overdue = tasks.filter((task) => dueState(task, now) === 'overdue').length;
    const today = tasks.filter((task) => dueState(task, now) === 'today').length;
    const descriptor = TASK_PRIORITIES.find((item) => item.value === active) ?? TASK_PRIORITIES[0];
    const hint = descriptor.action.toLowerCase();

    return (
        <section className="dashboard-card">
            <div className="dashboard-card-head">
                <h3>Мои задачи <span className="pulse-muted">{tasks.length}</span></h3>
                <span className="pulse-due">
                    {overdue > 0 && <span className="pulse-red">просрочено {overdue}</span>}
                    {today > 0 && <span className="pulse-warn">сегодня {today}</span>}
                </span>
            </div>
            <div className="dashboard-flags">
                {PULSE_PRIORITIES.map((value) => {
                    const label = TASK_PRIORITIES.find((item) => item.value === value)?.label;
                    return (
                        <Tooltip key={value} title={label}>
                            <button
                                type="button"
                                className={`dashboard-flag${value === active ? ' active' : ''}`}
                                style={{ '--cell-color': PRIORITY_COLORS[value] }}
                                aria-label={`${label}: ${counts[value]}`}
                                aria-pressed={value === active}
                                onClick={() => { setPriority(value); setExpanded(false); }}
                            >
                                <FlagFilled />
                                <span className="dashboard-flag-count">{counts[value]}</span>
                            </button>
                        </Tooltip>
                    );
                })}
            </div>
            <div className="pulse-hint">{descriptor.label} — {hint}</div>
            {list.length === 0 && <div className="dashboard-empty">{loading ? 'Загрузка…' : 'Нет задач'}</div>}
            {(expanded ? list : list.slice(0, LIMIT)).map((task) => (
                <div key={task.id} className="pulse-task-row">
                    <WeekPlanStar active={weekPlan.ids.has(task.id)} onClick={() => weekPlan.toggle(task.id)} />
                    <button type="button" className="dashboard-task" onClick={() => onOpen(task)}>
                        <span className="dashboard-task-name">{task.name}</span>
                        <span className={`dashboard-task-meta${dueState(task, now) === 'overdue' ? ' overdue' : ''}`}>
                            {[showCompany && task.companyName, task.projectName, task.planDate && `срок ${fmtDate(task.planDate)}`]
                                .filter(Boolean).join(' · ')}
                        </span>
                    </button>
                </div>
            ))}
            {list.length > LIMIT && (
                <button type="button" className="dashboard-all pulse-more" onClick={() => setExpanded((v) => !v)}>
                    {expanded ? 'Свернуть' : `Все ${list.length}`}
                </button>
            )}
        </section>
    );
};

// Таблица проектов или сотрудников: строки без открытых задач свёрнуты
const PulseSection = ({ title, rows, columns, rowKey, emptyText, loading, onRowClick }) => {
    const [showIdle, setShowIdle] = useState(false);
    const active = rows.filter((r) => r.health.level !== 'idle');
    const idle = rows.length - active.length;

    return (
        <section className="dashboard-card">
            <div className="dashboard-card-head"><h3>{title}</h3></div>
            <Table
                size="small"
                rowKey={rowKey}
                dataSource={showIdle ? rows : active}
                columns={columns}
                pagination={false}
                loading={loading}
                locale={{ emptyText }}
                rowClassName={() => 'pulse-row'}
                onRow={(r) => ({ onClick: () => onRowClick(r) })}
                scroll={{ x: 'max-content' }}
            />
            {idle > 0 && (
                <button type="button" className="dashboard-all pulse-more" onClick={() => setShowIdle((v) => !v)}>
                    {showIdle ? 'Скрыть без открытых задач' : `Без открытых задач: ${idle}`}
                </button>
            )}
        </section>
    );
};

// Пульс суперадмина: мои задачи, проекты и сотрудники по всем активным фирмам
const Pulse = () => {
    const user = useSelector((s) => s.user);
    const navigate = useNavigate();
    const selectCompany = useSelectCompany();
    const [searchParams, setSearchParams] = useSearchParams();
    const [filters, setFilter] = useUrlFilters(FILTER_SPEC, 'pulseFilters');
    const [data, setData] = useState(null);
    const [loadedAt, setLoadedAt] = useState(null);
    const [loading, setLoading] = useState(false);
    const [openTask, setOpenTask] = useState(null);
    const [editContragent, setEditContragent] = useState(null);
    const [planKey, setPlanKey] = useState(0);
    const reloadPlan = useCallback(() => setPlanKey((k) => k + 1), []);
    const weekPlan = useWeekPlanIds({ allCompanies: true, onChange: reloadPlan });

    const load = useCallback(() => {
        setLoading(true);
        serverFetch('pulse')
            .then((d) => {
                setData(d);
                setLoadedAt(dayjs());
            })
            .catch(showError)
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    // ?task=&tc= — задача, открытая после переключения фирмы: показать карточку и убрать параметры из адреса
    useEffect(() => {
        const id = Number(searchParams.get('task'));
        if (!id) return;
        if (Number(searchParams.get('tc')) === user.companyId) setOpenTask(id);
        const qs = new URLSearchParams(searchParams);
        qs.delete('task');
        qs.delete('tc');
        setSearchParams(qs, { replace: true });
    }, [searchParams, setSearchParams, user.companyId]);

    const companyId = filters.companyId;
    const now = useMemo(() => loadedAt ?? dayjs(), [loadedAt]);
    const inCompany = useCallback(
        (rows) => (rows ?? []).filter((r) => !companyId || r.companyId === companyId),
        [companyId],
    );
    const myTasks = useMemo(() => ratedOpen(inCompany(data?.myTasks)), [data, inCompany]);
    const projects = useMemo(
        () => rankRows(inCompany(data?.projects), now, (r) => (r.projectId == null ? 'Задачи без проекта' : null)),
        [data, inCompany, now],
    );
    const users = useMemo(
        () => rankRows(inCompany(data?.users), now, (r) => (r.userId == null ? 'Задачи без исполнителя' : null)),
        [data, inCompany, now],
    );

    const companies = data?.companies ?? [];
    const { shown, hidden } = splitCompanies(companies, companyId);
    const total = Object.fromEntries(COUNTER_KEYS.map((key) => [key, companies.reduce((sum, c) => sum + (c[key] || 0), 0)]));

    // Задача чужой фирмы: переключаем фирму и возвращаемся на Пульс с открытой карточкой
    const onOpenTask = (task) => {
        if (task.companyId === user.companyId) {
            setOpenTask(task.id);
            return;
        }
        const qs = new URLSearchParams(searchParams);
        qs.set('task', task.id);
        qs.set('tc', task.companyId);
        selectCompany({ id: task.companyId, name: task.companyName }, `/pulse?${qs.toString()}`);
    };

    const closeTask = () => {
        setOpenTask(null);
        load();
        reloadPlan();
        weekPlan.reload();
    };

    const openJournal = (row, query) => {
        const url = `/tasks?${query}`;
        if (row.companyId === user.companyId) navigate(url);
        else selectCompany({ id: row.companyId, name: row.companyName }, url);
    };

    const nameCell = (r, { orphan, tag, sub }) => (
        <div className="pulse-cell">
            <span className="pulse-name">
                <HealthDot health={r.health} />
                <span className={`pulse-name-text${orphan ? ' pulse-orphan' : ''}`}>{r.name}</span>
                {tag}
                {!companyId && <span className="pulse-muted">{r.companyName}</span>}
            </span>
            {sub && <span className="pulse-sub">{sub}</span>}
        </div>
    );

    const counterColumns = (filterOf) => [
        { title: 'Открыто', dataIndex: 'qtyOpen', align: 'right', render: (v) => <Count value={v} /> },
        {
            title: 'Просрочено',
            dataIndex: 'qtyOld',
            align: 'right',
            render: (v, r) => (v > 0 ? (
                <button
                    type="button"
                    className="pulse-link pulse-red"
                    title="Открыть просроченные"
                    onClick={(e) => { e.stopPropagation(); openJournal(r, `vid=4&${filterOf(r)}`); }}
                >
                    {v}
                </button>
            ) : <Count value={0} />),
        },
        {
            title: <Tooltip title="Закрыто за последние 7 дней">Закрыто 7 дн</Tooltip>,
            dataIndex: 'qtyDone7',
            align: 'right',
            render: (v) => <Count value={v} className="pulse-green" />,
        },
        {
            title: <Tooltip title="Последнее изменение или комментарий по задачам">Движение</Tooltip>,
            dataIndex: 'lastActivity',
            render: (v, r) => (
                <Tooltip title={fmtDateTime(v)}>
                    <span className={`pulse-nowrap${r.qtyOpen > 0 && isStale(v, now) ? ' pulse-warn' : ''}`}>
                        {activityLabel(v, now)}
                    </span>
                </Tooltip>
            ),
        },
    ];

    const projectColumns = [
        {
            title: 'Проект',
            key: 'name',
            render: (_, r) => nameCell(r, {
                orphan: r.projectId == null,
                tag: r.isClosed && <Tag className="pulse-tag">закрыт</Tag>,
            }),
        },
        ...counterColumns(projectFilter),
        {
            title: <Tooltip title="Ближайшая версия: номер · дата выпуска · открытых задач">Релиз</Tooltip>,
            key: 'release',
            render: (_, r) => (r.nextVersionName ? (
                <span className={`pulse-release${isReleaseOverdue(r, now) ? ' overdue' : ''}`}>
                    {r.nextVersionName} · {fmtDate(r.nextVersionDate)}
                    {r.nextVersionOpen > 0 ? ` · осталось ${r.nextVersionOpen}` : ''}
                </span>
            ) : null),
        },
    ];

    const userColumns = [
        {
            title: 'Сотрудник',
            key: 'name',
            render: (_, r) => nameCell(r, {
                orphan: r.userId == null,
                tag: !r.active && <Tag className="pulse-tag">неактивен</Tag>,
                sub: r.userId != null && r.qtyNoDate > 0 ? `без срока: ${r.qtyNoDate}` : null,
            }),
        },
        ...counterColumns(userFilter),
    ];

    return (
        <div className="pulse">
            <div className="pulse-head">
                <h2 className="dashboard-title">Пульс</h2>
                <div className="pulse-companies">
                    <Tooltip title={companyHint(total)}>
                        <Tag.CheckableTag
                            className="pulse-chip"
                            checked={!companyId}
                            onChange={() => setFilter({ companyId: 0 })}
                        >
                            Все фирмы
                            {total.qtyOld > 0 && <Badge count={total.qtyOld} size="small" overflowCount={999} />}
                        </Tag.CheckableTag>
                    </Tooltip>
                    {shown.map((c) => (
                        <Tooltip key={c.companyId} title={companyHint(c)}>
                            <Tag.CheckableTag
                                className="pulse-chip"
                                checked={companyId === c.companyId}
                                onChange={() => setFilter({ companyId: companyId === c.companyId ? 0 : c.companyId })}
                            >
                                {c.companyName}
                                {c.qtyOld > 0 && <Badge count={c.qtyOld} size="small" overflowCount={999} />}
                            </Tag.CheckableTag>
                        </Tooltip>
                    ))}
                    {hidden.length > 0 && (
                        <Tooltip title={hidden.map((c) => c.companyName).join(', ')}>
                            <span className="pulse-muted">ещё {hidden.length} без задач</span>
                        </Tooltip>
                    )}
                </div>
                {loadedAt && <span className="pulse-muted">обновлено {loadedAt.format('HH:mm')}</span>}
                <Tooltip title="Обновить">
                    <Button size="small" icon={<ReloadOutlined />} loading={loading} onClick={load} aria-label="Обновить" />
                </Tooltip>
            </div>
            <WeekPlan onOpen={onOpenTask} reloadKey={planKey} allCompanies onChange={weekPlan.reload} />
            <div className="pulse-grid">
                <MyTasks
                    tasks={myTasks}
                    now={now}
                    showCompany={!companyId}
                    loading={loading && !data}
                    onOpen={onOpenTask}
                    weekPlan={weekPlan}
                />
                <div>
                    <PulseSection
                        title="Проекты"
                        rows={projects}
                        columns={projectColumns}
                        rowKey={(r) => `${r.companyId}:${r.projectId ?? 0}`}
                        emptyText="Нет проектов с открытыми задачами"
                        loading={loading && !data}
                        onRowClick={(r) => openJournal(r, `vid=3&${projectFilter(r)}`)}
                    />
                    <PulseSection
                        title="Сотрудники"
                        rows={users}
                        columns={userColumns}
                        rowKey={(r) => `${r.companyId}:${r.userId ?? 0}`}
                        emptyText="Нет сотрудников с открытыми задачами"
                        loading={loading && !data}
                        onRowClick={(r) => openJournal(r, `vid=3&${userFilter(r)}`)}
                    />
                </div>
            </div>
            {openTask !== null && (
                <Task id={openTask} onClose={closeTask} onOpenContragent={setEditContragent} />
            )}
            {editContragent !== null && (
                <Contragent id={editContragent} onClose={() => setEditContragent(null)} />
            )}
        </div>
    );
};

export default Pulse;
